/**
 * Insights API Service
 *
 * Provides data fetching and all analytics aggregation/transform functions
 * for the Insights page. Derives all data from the existing /estimates endpoint —
 * no new backend Lambda required.
 */

import { estimatesApi } from './estimatesApi';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Shared color palette — consistent job-type colors across all charts */
export const JOB_TYPE_COLORS = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#f97316', // orange-500
  '#ec4899', // pink-500
];

const DEFAULT_FILTERS = {
  location: '',
  timePeriod: '1yr',
  startDate: '',
  endDate: '',
  jobTypes: [],
  categories: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

/** Compute arithmetic mean of a numeric array. Returns 0 for empty arrays. */
function mean(arr) {
  if (!arr || arr.length === 0) return 0;
  return arr.reduce((sum, v) => sum + v, 0) / arr.length;
}

/**
 * Normalize a job type string to a consistent title-cased form.
 * "warehouse" and "Warehouse" both become "Warehouse".
 */
function normalizeJobType(raw) {
  if (!raw) return 'Other';
  return raw.trim().replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─────────────────────────────────────────────────────────────────────────────
// Data Fetching
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch all estimate pages matching the API-level filter (location only).
 * jobType, categories, and time period are filtered client-side.
 *
 * @param {object} apiFilters - { location }
 * @returns {{ estimates: Array, jobTypes: Array }}
 */
export async function fetchAllEstimates(apiFilters = {}) {
  const PAGE_SIZE = 100;
  let page = 1;
  let allEstimates = [];
  let total = null;
  let jobTypes = [];

  do {
    const options = {
      page,
      limit: PAGE_SIZE,
    };

    if (apiFilters.location) {
      options.location = apiFilters.location;
    }

    const response = await estimatesApi.getEstimates(options);

    const estimates = response.estimates || response || [];
    allEstimates = allEstimates.concat(estimates);

    // Capture total from first response
    if (total === null) {
      total = response.total ?? estimates.length;
      jobTypes = response.jobTypes || [];
    }

    page++;

    // Safety: break if we got nothing new (prevents infinite loop)
    if (estimates.length === 0) break;
  } while (allEstimates.length < (total || 0));

  return { estimates: allEstimates, jobTypes };
}

/**
 * Map UI filter state to API query params (only location goes to the API).
 * @param {object} uiFilters
 * @returns {object} API params
 */
export function buildApiFilters(uiFilters) {
  const params = {};
  if (uiFilters.location) {
    params.location = uiFilters.location;
  }
  return params;
}

// ─────────────────────────────────────────────────────────────────────────────
// Client-Side Filtering
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Apply jobTypes, categories, timePeriod, startDate, endDate filters in JavaScript.
 * @param {Array} estimates
 * @param {object} uiFilters
 * @returns {Array}
 */
export function applyClientFilters(estimates, uiFilters) {
  let result = estimates;

  // Job type multi-select (case-insensitive via normalization)
  if (uiFilters.jobTypes && uiFilters.jobTypes.length > 0) {
    result = result.filter((e) =>
      uiFilters.jobTypes.includes(normalizeJobType(e.jobType))
    );
  }

  // Category filter — keep estimate if ANY of its categories match a selected category
  if (uiFilters.categories && uiFilters.categories.length > 0) {
    result = result.filter((e) =>
      (e.categories || []).some((cat) =>
        uiFilters.categories.includes(cat.categoryName?.trim())
      )
    );
  }

  // Time period
  if (uiFilters.timePeriod && uiFilters.timePeriod !== 'all') {
    const cutoff = getDateCutoff(uiFilters.timePeriod, uiFilters.startDate);
    if (cutoff) {
      result = result.filter((e) => e.estimateDate && new Date(e.estimateDate) >= cutoff);
    }
  }

  // Custom end date
  if (uiFilters.timePeriod === 'custom' && uiFilters.endDate) {
    const endDate = new Date(uiFilters.endDate);
    endDate.setHours(23, 59, 59, 999);
    result = result.filter((e) => e.estimateDate && new Date(e.estimateDate) <= endDate);
  }

  return result;
}

/**
 * Compute the start date cutoff for a given time period.
 * @param {string} timePeriod
 * @param {string} customStart - ISO string for 'custom' period
 * @returns {Date|null}
 */
function getDateCutoff(timePeriod, customStart) {
  const now = new Date();
  switch (timePeriod) {
    case '3mo': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 3);
      return d;
    }
    case '6mo': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 6);
      return d;
    }
    case '1yr': {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    }
    case 'custom':
      return customStart ? new Date(customStart) : null;
    default:
      return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Metadata Extraction
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extract unique state abbreviations from location strings like "Cincinnati, OH".
 * @param {Array} estimates
 * @returns {string[]} Sorted state abbreviations
 */
export function extractUniqueLocations(estimates) {
  const states = new Set();
  estimates.forEach((e) => {
    if (e.location) {
      const state = parseState(e.location);
      if (state) states.add(state);
    }
  });
  return [...states].sort();
}

/**
 * Parse state abbreviation from a location string.
 * Handles "Cincinnati, OH", "Columbus OH", "OH", etc.
 * @param {string} location
 * @returns {string|null}
 */
function parseState(location) {
  if (!location) return null;
  // Try "City, ST" format first
  const commaMatch = location.match(/,\s*([A-Z]{2})\s*$/);
  if (commaMatch) return commaMatch[1];
  // Try any 2-letter all-caps token
  const tokens = location.split(/[\s,]+/);
  const stateToken = tokens.find((t) => /^[A-Z]{2}$/.test(t));
  return stateToken || null;
}

/**
 * Extract all unique job types from estimates, normalized to title case.
 * Deduplicates case variants (e.g., "warehouse" and "Warehouse" → "Warehouse").
 * @param {Array} estimates
 * @returns {string[]} Sorted, deduplicated, title-cased job types
 */
export function extractUniqueJobTypes(estimates) {
  const types = new Set();
  estimates.forEach((e) => {
    if (e.jobType) types.add(normalizeJobType(e.jobType));
  });
  return [...types].sort();
}

/**
 * Extract all unique category names from estimates' categories arrays.
 * @param {Array} estimates
 * @returns {string[]} Sorted category names
 */
export function extractUniqueCategories(estimates) {
  const cats = new Set();
  estimates.forEach((e) => {
    (e.categories || []).forEach((cat) => {
      if (cat.categoryName) cats.add(cat.categoryName.trim());
    });
  });
  return [...cats].sort();
}

// ─────────────────────────────────────────────────────────────────────────────
// KPI Aggregation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute summary KPIs from filtered estimates.
 * @param {Array} estimates
 * @returns {{ totalProjects, avgCostPerSf, totalValue }}
 */
export function computeKPIs(estimates) {
  const costPerSfValues = estimates.map((e) => e.costPerSf).filter((v) => v != null && v > 0);

  return {
    totalProjects: estimates.length,
    avgCostPerSf: costPerSfValues.length > 0 ? mean(costPerSfValues) : null,
    totalValue: estimates.reduce((sum, e) => sum + (e.totalCost || 0), 0),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Chart Data Transforms
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build data for the Cost/SF Over Time area chart.
 * Groups estimates by month and job type, computing avg estimated cost/SF per cell.
 * costPerSf = total_cost / square_footage (estimated, not actual).
 *
 * @param {Array} estimates
 * @returns {{ data: Array<{month: string, ...jobTypeKeys: number}>, jobTypes: string[] }}
 */
export function buildCostTrendData(estimates) {
  const byMonth = {};
  const allJobTypes = new Set();

  estimates
    .filter((e) => e.estimateDate && e.costPerSf != null && e.costPerSf > 0)
    .forEach((e) => {
      const normalizedType = normalizeJobType(e.jobType);
      const d = new Date(e.estimateDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = {};
      if (!byMonth[key][normalizedType]) byMonth[key][normalizedType] = [];
      byMonth[key][normalizedType].push(e.costPerSf);
      allJobTypes.add(normalizedType);
    });

  const sortedMonths = Object.keys(byMonth).sort();
  const jobTypes = [...allJobTypes].sort();

  const data = sortedMonths.map((key) => {
    const [year, month] = key.split('-');
    const label = new Date(Number(year), Number(month) - 1).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
    const row = { month: label };
    jobTypes.forEach((jt) => {
      if (byMonth[key][jt] && byMonth[key][jt].length > 0) {
        row[jt] = parseFloat(mean(byMonth[key][jt]).toFixed(2));
      }
    });
    return row;
  });

  return { data, jobTypes };
}

/**
 * Build data for the Category Cost Breakdown horizontal bar chart.
 * Flattens all categories from all estimates, groups by name, computes avg cost/SF.
 * Returns top 10 by avgCostPerSf descending.
 *
 * @param {Array} estimates
 * @returns {Array<{category: string, avgCostPerSf: number, avgSubtotal: number, count: number}>}
 */
export function buildCategoryBreakdownData(estimates) {
  const byCategory = {};

  estimates.forEach((e) => {
    if (!e.categories || !Array.isArray(e.categories)) return;
    e.categories.forEach((cat) => {
      const name = cat.categoryName;
      if (!name) return;
      if (!byCategory[name]) byCategory[name] = { costPerSfValues: [], subtotalValues: [] };
      if (cat.costPerSf != null && cat.costPerSf > 0) {
        byCategory[name].costPerSfValues.push(cat.costPerSf);
      }
      if (cat.subtotal != null && cat.subtotal > 0) {
        byCategory[name].subtotalValues.push(cat.subtotal);
      }
    });
  });

  return Object.entries(byCategory)
    .map(([category, { costPerSfValues, subtotalValues }]) => ({
      category,
      avgCostPerSf:
        costPerSfValues.length > 0 ? parseFloat(mean(costPerSfValues).toFixed(2)) : 0,
      avgSubtotal:
        subtotalValues.length > 0 ? Math.round(mean(subtotalValues)) : 0,
      count: costPerSfValues.length,
    }))
    .filter((d) => d.avgCostPerSf > 0)
    .sort((a, b) => b.avgCostPerSf - a.avgCostPerSf)
    .slice(0, 10);
}

/**
 * Build data for the Job Type Distribution bar chart.
 * Groups estimates by normalized job type, computing count, avg cost/SF, and total value.
 *
 * @param {Array} estimates
 * @returns {Array<{jobType: string, count: number, totalValue: number, avgCostPerSf: number}>}
 */
export function buildJobTypeDistributionData(estimates) {
  const byType = {};

  estimates.forEach((e) => {
    const type = normalizeJobType(e.jobType);
    if (!byType[type]) byType[type] = { count: 0, totalValue: 0, costPerSfValues: [] };
    byType[type].count++;
    byType[type].totalValue += e.totalCost || 0;
    if (e.costPerSf != null && e.costPerSf > 0) {
      byType[type].costPerSfValues.push(e.costPerSf);
    }
  });

  return Object.entries(byType)
    .map(([jobType, { count, totalValue, costPerSfValues }]) => ({
      jobType,
      count,
      totalValue,
      avgCostPerSf:
        costPerSfValues.length > 0 ? parseFloat(mean(costPerSfValues).toFixed(2)) : 0,
    }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Build data for the Geography horizontal bar chart.
 * Parses state abbreviation from each estimate's location string,
 * groups by state, counts projects, and computes avg cost/SF.
 *
 * @param {Array} estimates
 * @returns {Array<{state: string, count: number, avgCostPerSf: number}>}
 */
export function buildGeographyData(estimates) {
  const byState = {};

  estimates.forEach((e) => {
    const state = parseState(e.location);
    if (!state) return;
    if (!byState[state]) byState[state] = { count: 0, costPerSfValues: [] };
    byState[state].count++;
    if (e.costPerSf != null && e.costPerSf > 0) {
      byState[state].costPerSfValues.push(e.costPerSf);
    }
  });

  return Object.entries(byState)
    .map(([state, { count, costPerSfValues }]) => ({
      state,
      count,
      avgCostPerSf:
        costPerSfValues.length > 0 ? parseFloat(mean(costPerSfValues).toFixed(2)) : 0,
    }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Build data for the Average Fee Amount Over Time area chart.
 * The fee category has categoryCode === '00'. Computes avg fee subtotal (dollars) per month.
 *
 * @param {Array} estimates
 * @returns {Array<{month: string, avgFeeAmount: number, count: number}>}
 */
export function buildAvgFeeAmountTrendData(estimates) {
  const byMonth = {};

  estimates
    .filter((e) => e.estimateDate && Array.isArray(e.categories) && e.categories.length > 0)
    .forEach((e) => {
      const feeCat = e.categories.find((c) => c.categoryCode === '00');
      if (!feeCat || feeCat.subtotal == null || feeCat.subtotal <= 0) return;

      const d = new Date(e.estimateDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = [];
      byMonth[key].push(feeCat.subtotal);
    });

  return Object.keys(byMonth)
    .sort()
    .map((key) => {
      const [year, month] = key.split('-');
      const label = new Date(Number(year), Number(month) - 1).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
      return {
        month: label,
        avgFeeAmount: Math.round(mean(byMonth[key])),
        count: byMonth[key].length,
      };
    });
}

/**
 * Build data for the Average Fee per SF Over Time line chart.
 * The fee category has categoryCode === '00'. Computes avg fee cost/SF per month.
 *
 * @param {Array} estimates
 * @returns {Array<{month: string, avgFeeCostPerSf: number, count: number}>}
 */
export function buildFeeTrendData(estimates) {
  const byMonth = {};

  estimates
    .filter((e) => e.estimateDate && Array.isArray(e.categories) && e.categories.length > 0)
    .forEach((e) => {
      const feeCat = e.categories.find((c) => c.categoryCode === '00');
      if (!feeCat || feeCat.costPerSf == null || feeCat.costPerSf <= 0) return;

      const d = new Date(e.estimateDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = [];
      byMonth[key].push(feeCat.costPerSf);
    });

  return Object.keys(byMonth)
    .sort()
    .map((key) => {
      const [year, month] = key.split('-');
      const label = new Date(Number(year), Number(month) - 1).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
      return {
        month: label,
        avgFeeCostPerSf: parseFloat(mean(byMonth[key]).toFixed(2)),
        count: byMonth[key].length,
      };
    });
}

export { DEFAULT_FILTERS };
